<template>
  <div class="report-page">
    <h1 class="visually-hidden">{{ isHistorical ? '历史评估报告' : '骨干人才保留建议报告' }}</h1>
    <div class="report-headline">
      <el-page-header
        @back="$router.push('/select')"
        :content="isHistorical ? '历史评估报告' : '骨干人才保留建议报告'"
        class="page-back-header"
      />
      <div class="report-top-actions">
        <el-dropdown trigger="click" :disabled="!employeeEvalHistory.length" @command="openHistoricalEval">
          <el-button class="history-top-btn">历史记录</el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="item in employeeEvalHistory"
              :key="item.evaluation_id"
              :command="item.evaluation_id"
            >
              {{ formatHistoryItem(item) }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-button v-if="!reportLoading" class="btn-action share-top-btn" icon="el-icon-share" @click="shareReport">分享报告</el-button>
      </div>
    </div>

    <el-alert
      v-if="reportLoading"
      title="骨干保留专家正在生成报告..."
      type="info"
      :closable="false"
      class="report-alert"
      show-icon
    />
    <el-alert
      v-else-if="reportSource === 'rule_fallback'"
      title="智能生成暂不可用，当前展示规则报告"
      type="warning"
      :closable="false"
      class="report-alert"
      show-icon
    />

    <!-- Hero 综合信息卡 -->
    <div class="hero-card animate-fade-in-up">
      <div class="hero-left">
        <div class="hero-avatar">
          {{ employee.real_name ? employee.real_name[0] : '?' }}
        </div>
        <div class="hero-info">
          <h2 class="hero-name">{{ employee.real_name }}</h2>
          <div class="hero-meta">
            {{ employee.dept_name || '-' }} · {{ employee.job_title || '-' }}
          </div>
        </div>
      </div>
      <div class="hero-gauge">
        <div class="gauge-ring">
          <svg width="100" height="100" viewBox="0 0 100 100">
            <circle class="gauge-bg" cx="50" cy="50" r="45"/>
            <circle
              class="gauge-fill"
              cx="50" cy="50" r="45"
              :stroke="gaugeColor"
              :stroke-dashoffset="gaugeOffset"
            />
          </svg>
          <div class="gauge-text">
            <div class="gauge-num">{{ report.total_score }}</div>
            <div class="gauge-label">风险分</div>
          </div>
        </div>
        <div class="gauge-risk">
          <div class="risk-tag" :class="'risk-' + report.risk_level">{{ riskLevelText }}</div>
          <div class="risk-hint">{{ riskHint }}</div>
        </div>
      </div>
    </div>

    <div v-if="reportLoading" class="generation-panel animate-fade-in-up">
      <div class="generation-header">
        <div class="generation-icon">
          <i class="el-icon-loading"></i>
        </div>
        <div>
          <div class="generation-title">骨干保留专家正在生成保留建议报告</div>
          <div class="generation-subtitle">基于领先大模型技术，结合三悟骨干保留数据、行为画像和后台内容库，综合研究生成管理建议。</div>
        </div>
      </div>
      <div class="generation-steps">
        <div class="generation-step active">
          <span class="step-dot"></span>
          <span>读取员工风险画像</span>
        </div>
        <div class="generation-step active">
          <span class="step-dot"></span>
          <span>匹配管理建议、案例和课程</span>
        </div>
        <div class="generation-step">
          <span class="step-dot"></span>
          <span>生成专家建议表达</span>
        </div>
      </div>
      <div class="generation-skeleton">
        <div class="skeleton-line wide"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line short"></div>
      </div>
    </div>

    <el-tooltip v-if="!reportLoading" :content="modelAuthorityTip" placement="top" effect="light">
      <div class="report-authority">
        本报告由 <span>骨干保留专家</span> 为你生成
      </div>
    </el-tooltip>

    <div v-if="!reportLoading" class="report-module-shell animate-fade-in-up">
      <div class="module-switcher">
        <button
          v-for="tab in reportModuleTabs"
          :key="tab.key"
          type="button"
          class="module-tab"
          :class="{ active: activeReportModule === tab.key }"
          @click="setActiveModule(tab.key)"
        >
          <span class="module-label">{{ tab.label }}</span>
          <span class="module-count">{{ tab.count }}</span>
        </button>
      </div>
    </div>

    <!-- 历史评估详情：已勾选行为列表 -->
    <div
      v-if="!reportLoading && activeReportModule === 'behavior_detail' && modulesConfig.behavior_detail && isHistorical && answerDetails.length"
      id="section-answers"
      class="report-card animate-fade-in-up"
      style="animation-delay: 0.15s"
    >
      <div class="card-header">
        <h3 class="card-title">本次评估勾选详情</h3>
        <span class="section-count">{{ answeredYesCount }} 项 / 共 {{ answerDetails.length }} 项</span>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div class="behavior-answers">
          <div
            v-for="(item, i) in answerDetails"
            :key="i"
            class="behavior-answer-item"
            :class="'answer-' + item.answer"
          >
            <span class="answer-number">{{ i + 1 }}</span>
            <span class="answer-desc">{{ item.category_name }}：{{ item.behavior_desc }}</span>
            <el-tag size="mini" :type="item.answer === 1 ? 'danger' : (item.answer === 2 ? 'success' : 'info')">
              {{ item.answer === 1 ? '是 +' + item.score + '分' : (item.answer === 2 ? '否' : '不了解') }}
            </el-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块1: 风险归因分析 -->
    <div v-if="!reportLoading && activeReportModule === 'risk_analysis' && modulesConfig.risk_analysis" id="section-risk" class="report-card animate-fade-in-up" style="animation-delay: 0.2s">
      <div class="card-header">
        <h3 class="card-title">风险归因分析</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div class="risk-summary">
          {{ riskAnalysisSummary }}
        </div>

        <div class="risk-bars">
          <div v-for="(bar, i) in riskBars" :key="i" class="risk-bar-item">
            <div class="bar-label">{{ bar.label }}</div>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: bar.value + '%', background: bar.color }"></div>
            </div>
            <div class="bar-val">{{ bar.value }}%</div>
          </div>
        </div>

        <div class="risk-reasons">
          <div v-for="(reason, i) in riskReasons" :key="i" class="risk-reason">
            <span class="reason-number">{{ i + 1 }}</span>
            <span class="reason-text" v-html="formatReason(reason)"></span>
          </div>
        </div>

        <div v-if="yesBehaviorTags.length" class="behavior-tags-section">
          <div class="behavior-tags-title">本次命中行为标签</div>
          <div class="behavior-tags-list">
            <el-tag v-for="(tag, i) in yesBehaviorTags" :key="i" size="small" type="warning" effect="plain" class="behavior-tag">
              {{ tag }}
            </el-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块2: 下一步管理建议 -->
    <div v-if="!reportLoading && activeReportModule === 'suggestions' && modulesConfig.suggestions" id="section-suggestion" class="report-card animate-fade-in-up" style="animation-delay: 0.3s">
      <div class="card-header">
        <h3 class="card-title">下一步管理建议</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedSuggestions.length" class="report-item-list">
          <div
            v-for="(item, i) in matchedSuggestions"
            :key="i"
            class="report-item-card"
          >
            <div class="item-index">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="item-body">
              <div class="item-title">{{ item.title }}</div>
              <div class="item-content">{{ item.content }}</div>
              <button type="button" class="item-action" @click="openItemDetail('管理建议', item)">查看详细方案</button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无匹配的管理建议" :image-size="60" />
      </div>
    </div>

    <!-- 模块3: 过往公司内部成功案例 -->
    <div v-if="!reportLoading && activeReportModule === 'cases' && modulesConfig.cases" id="section-case" class="report-card animate-fade-in-up" style="animation-delay: 0.4s">
      <div class="card-header">
        <h3 class="card-title">过往公司内部成功案例</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedCases.length" class="report-item-list">
          <div v-for="(c, i) in matchedCases" :key="i" class="report-item-card">
            <div class="item-index">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="item-body">
              <div class="item-title">{{ c.title }}</div>
              <div class="item-content">{{ c.content }}</div>
              <button type="button" class="item-action" @click="openItemDetail('成功案例', c)">查看案例详情</button>
            </div>
          </div>
        </div>
        <el-empty v-else description="案例库持续建设中" :image-size="60" />
      </div>
    </div>

    <!-- 模块4: 推荐管理课程 / 资源 -->
    <div v-if="!reportLoading && activeReportModule === 'courses' && modulesConfig.courses" id="section-course" class="report-card animate-fade-in-up" style="animation-delay: 0.5s">
      <div class="card-header">
        <h3 class="card-title">推荐管理课程 / 资源</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedCourses.length" class="report-item-list">
          <div
            v-for="(course, i) in matchedCourses"
            :key="i"
            class="report-item-card"
          >
            <div class="item-index">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="item-body">
              <div class="item-title">{{ course.title }}</div>
              <div class="item-content">{{ course.content }}</div>
              <button type="button" class="item-action" @click="openItemDetail('推荐课程', course)">查看课程资源</button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无推荐课程" :image-size="60" />
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'EvaluationReport',
  created() {
    this.loadModulesConfig()
    this.loadGeneratedReport()
  },
  computed: {
    employee() {
      const id = parseInt(this.$route.params.id)
      const emp = this.$store.getters.employeeById(id)
      return emp || {}
    },
    isHistorical() {
      return !!this.$route.query.evaluation_id
    },
    historicalEval() {
      const evalId = parseInt(this.$route.query.evaluation_id)
      if (!evalId) return null
      const list = this.$store.state.evaluations || []
      return list.find(e => e.evaluation_id === evalId) || null
    },
    employeeEvalHistory() {
      const employeeId = this.employee.employee_id || parseInt(this.$route.params.id)
      if (!employeeId) return []
      return (this.$store.state.evaluations || [])
        .filter(item => item.employee_id === employeeId)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    },
    report() {
      if (this.historicalEval) {
        return {
          total_score: this.historicalEval.total_score,
          risk_level: this.historicalEval.risk_level,
          risk_analysis: this.historicalEval.risk_analysis || this.defaultReport.risk_analysis
        }
      }
      const current = this.$store.state.currentEvaluation
      if (current && current.employee_id === this.employee.employee_id) {
        return {
          total_score: current.total_score,
          risk_level: current.risk_level,
          risk_analysis: current.risk_analysis || this.defaultReport.risk_analysis
        }
      }
      return this.defaultReport
    },
    generatedReport() {
      const evalId = parseInt(this.$route.query.evaluation_id)
      const current = this.$store.state.currentAIReport
      if (current && current.evaluation_id === evalId) {
        return current
      }
      return this.aiReport
    },
    aiContent() {
      return (this.generatedReport && this.generatedReport.content) || {}
    },
    riskAnalysisSummary() {
      const risk = this.aiContent.risk_analysis || {}
      return risk.summary || this.report.risk_analysis || '暂无风险归因分析'
    },
    riskLevelText() {
      const level = this.report.risk_level
      return level === 3 ? '高风险' : (level === 2 ? '中风险' : '低风险')
    },
    riskHint() {
      const level = this.report.risk_level
      return level === 3 ? '建议立即干预' : (level === 2 ? '建议近期关注' : '风险可控')
    },
    reportTime() {
      if (this.historicalEval && this.historicalEval.created_at) {
        return this.historicalEval.created_at
      }
      return new Date().toLocaleString('zh-CN')
    },
    answerDetails() {
      if (this.historicalEval && this.historicalEval.answer_details) {
        return this.historicalEval.answer_details
      }
      const current = this.$store.state.currentEvaluation
      if (current && current.employee_id === this.employee.employee_id && current.answer_details) {
        return current.answer_details
      }
      return []
    },
    yesBehaviorIds() {
      return this.answerDetails.filter(d => d.answer === 1).map(d => d.behavior_id)
    },
    yesBehaviorTags() {
      return this.answerDetails.filter(d => d.answer === 1).map(d => d.behavior_desc).filter(Boolean)
    },
    answeredYesCount() {
      return this.answerDetails.filter(a => a.answer === 1).length
    },
    employeeBehaviorKeywords() {
      const details = this.answerDetails
      if (!details.length) return []
      const yesBehaviors = details.filter(d => d.answer === 1)
      return yesBehaviors.map(d => (d.behavior_desc || '') + ' ' + (d.category_name || '')).filter(Boolean)
    },
    matchedSuggestions() {
      const data = this.aiContent.suggestions
      if (data && Array.isArray(data.items)) return this.normalizeReportItems(data.items)
      if (this.historicalEval && this.historicalEval.matched_suggestions) return this.normalizeReportItems(this.historicalEval.matched_suggestions)
      const current = this.$store.state.currentEvaluation
      if (current && current.matched_suggestions) return this.normalizeReportItems(current.matched_suggestions)
      return []
    },
    matchedCases() {
      const data = this.aiContent.cases
      if (data && Array.isArray(data.items)) return this.normalizeReportItems(data.items)
      if (this.historicalEval && this.historicalEval.matched_cases) return this.normalizeReportItems(this.historicalEval.matched_cases)
      const current = this.$store.state.currentEvaluation
      if (current && current.matched_cases) return this.normalizeReportItems(current.matched_cases)
      return []
    },
    matchedCourses() {
      const data = this.aiContent.courses
      if (data && Array.isArray(data.items)) return this.normalizeReportItems(data.items)
      if (this.historicalEval && this.historicalEval.recommended_courses) return this.normalizeReportItems(this.historicalEval.recommended_courses)
      const current = this.$store.state.currentEvaluation
      if (current && current.recommended_courses) return this.normalizeReportItems(current.recommended_courses)
      return []
    },
    matchedFaqs() {
      return this.$store.getters.contentsByRiskAndKeywords(4, this.report.risk_level, this.yesBehaviorIds, this.employeeBehaviorKeywords)
    },
    gaugeColor() {
      const level = this.report.risk_level
      if (level === 3) return 'var(--color-high)'
      if (level === 2) return 'var(--color-medium)'
      return 'var(--color-low)'
    },
    gaugeOffset() {
      const circumference = 2 * Math.PI * 45
      const score = Math.min(Math.max(this.report.total_score || 0, 0), 100)
      return circumference * (1 - score / 100)
    },
    riskBars() {
      const factors = this.aiContent.risk_analysis && this.aiContent.risk_analysis.factors
      if (!Array.isArray(factors) || !factors.length) return []
      return factors.map((factor, index) => {
        const rawValue = factor.weight !== undefined ? Math.round(Number(factor.weight) * 100) : Number(factor.score || 0)
        return {
          label: factor.name || '风险因素',
          value: Math.max(0, Math.min(rawValue || 0, 100)),
          color: index === 0 ? 'var(--color-high)' : (index === 1 ? 'var(--color-medium)' : 'var(--color-text-muted)')
        }
      })
    },
    reportModuleTabs() {
      const tabs = []
      if (this.modulesConfig.risk_analysis) {
        tabs.push({ key: 'risk_analysis', label: '风险归因', count: this.riskBars.length || 1 })
      }
      if (this.modulesConfig.suggestions) {
        tabs.push({ key: 'suggestions', label: '管理建议', count: this.matchedSuggestions.length })
      }
      if (this.modulesConfig.cases) {
        tabs.push({ key: 'cases', label: '成功案例', count: this.matchedCases.length })
      }
      if (this.modulesConfig.courses) {
        tabs.push({ key: 'courses', label: '推荐课程', count: this.matchedCourses.length })
      }
      if (this.modulesConfig.behavior_detail && this.isHistorical && this.answerDetails.length) {
        tabs.push({ key: 'behavior_detail', label: '行为明细', count: this.answeredYesCount })
      }
      return tabs
    }
  },
  data() {
    return {
      aiReport: null,
      reportLoading: false,
      reportSource: '',
      activeReportModule: 'risk_analysis',
      modulesConfig: {
        risk_analysis: true,
        behavior_detail: true,
        suggestions: true,
        cases: true,
        courses: true,
        talking_points: true
      },
      defaultReport: {
        total_score: 0,
        risk_level: 1,
        risk_analysis: ''
      },
      riskReasons: [],
      modelAuthorityTip: '基于领先大模型技术，结合三悟骨干保留数据与行为画像，综合研究生成管理建议。'
    }
  },
  methods: {
    async loadModulesConfig() {
      const taskId = this.resolveTaskId()
      try {
        const res = await this.$api.fetchAiReportModules(taskId ? { task_id: taskId } : {})
        if (res.data) {
          this.modulesConfig = {
            ...this.modulesConfig,
            risk_analysis: res.data.risk_analysis,
            behavior_detail: res.data.behavior_detail,
            suggestions: res.data.suggestions,
            cases: res.data.cases,
            courses: res.data.courses,
            talking_points: res.data.talking_points
          }
        }
        this.ensureActiveReportModule()
      } catch (e) {
        console.warn('[EvaluationReport] 加载 AI 报告模块配置失败:', e.message)
      }
    },
    async loadGeneratedReport() {
      const evalId = parseInt(this.$route.query.evaluation_id)
      if (!evalId) return
      const current = this.$store.state.currentAIReport
      if (current && current.evaluation_id === evalId && !this.hasGarbledReport(current)) {
        this.aiReport = current
        this.reportSource = current.source || ''
        this.ensureActiveReportModule()
        return
      }
      this.reportLoading = true
      try {
        const res = await this.$api.generateAiReport(evalId)
        this.aiReport = res.data
        this.reportSource = res.data && res.data.source ? res.data.source : ''
        this.$store.commit('SET_CURRENT_AI_REPORT', res.data)
        this.ensureActiveReportModule()
      } catch (e) {
        console.warn('[EvaluationReport] 生成 AI 报告失败:', e.message)
        this.reportSource = 'rule_fallback'
      } finally {
        this.reportLoading = false
      }
    },
    resolveTaskId() {
      if (this.historicalEval && this.historicalEval.task_id) return this.historicalEval.task_id
      const current = this.$store.state.currentEvaluation
      if (current && current.task_id) return current.task_id
      if (this.employee && this.employee.task_id) return this.employee.task_id
      return this.$store.getters.currentTaskId
    },
    setActiveModule(key) {
      this.activeReportModule = key
    },
    openHistoricalEval(evaluationId) {
      if (!evaluationId) return
      this.$router.push('/report/' + this.employee.employee_id + '?evaluation_id=' + evaluationId)
    },
    formatHistoryItem(item) {
      const time = item.created_at ? String(item.created_at).replace('T', ' ').slice(0, 16) : '-'
      const level = item.risk_level === 3 ? '高风险' : (item.risk_level === 2 ? '中风险' : '低风险')
      return `${item.employee_name || this.employee.real_name || '员工'} · ${time} · ${level}`
    },
    ensureActiveReportModule() {
      this.$nextTick(() => {
        const available = this.reportModuleTabs.map(tab => tab.key)
        if (available.length && !available.includes(this.activeReportModule)) {
          this.activeReportModule = available[0]
        }
      })
    },
    normalizeReportItems(items) {
      return (items || []).map((item, index) => {
        if (typeof item === 'string') {
          return { title: '建议' + (index + 1), content: item, qr_url: '' }
        }
        return {
          ...item,
          title: item.title || item.name || ('内容' + (index + 1)),
          content: item.content || item.summary || item.desc || '',
          qr_url: item.qr_url || item.url || ''
        }
      })
    },
    hasGarbledReport(report) {
      const content = report && report.content ? report.content : {}
      return ['suggestions', 'cases', 'courses'].some(key => {
        const items = content[key] && content[key].items
        if (!Array.isArray(items)) return false
        return items.some(item => this.hasGarbledText(item && (item.title || item.content || item.summary || item.desc || '')))
      })
    },
    hasGarbledText(text) {
      return typeof text === 'string' && /\?{3,}/.test(text)
    },
    openItemDetail(type, item) {
      const title = item.title || type
      const content = item.content || item.summary || item.desc || '暂无详情'
      this.$alert(content, title, {
        confirmButtonText: '知道了',
        customClass: 'report-detail-dialog'
      })
    },
    shareReport() {
      const url = window.location.href
      const input = document.createElement('input')
      input.value = url
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('报告链接已复制到剪贴板')
    },
    formatReason(text) {
      const idx = text.indexOf('：')
      if (idx > 0) {
        return '<strong>' + text.slice(0, idx + 1) + '</strong>' + text.slice(idx + 1)
      }
      return text
    }
  }
}
</script>

<style scoped>
/* ===== 页面基础 ===== */
.report-page {
  max-width: 840px;
  margin: 0 auto;
  padding: 24px 16px 48px;
  position: relative;
}

.report-headline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.report-top-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.page-back-header {
  flex: 1;
  min-width: 0;
}

/* ===== 渐入动画 ===== */
@keyframes fadeInUp {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
  animation: fadeInUp 0.5s ease-out forwards;
}

.report-alert {
  margin-bottom: 16px;
}

.generation-panel {
  padding: 22px 24px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  margin-bottom: 16px;
}

.generation-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.generation-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--color-primary-gradient);
  color: var(--color-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.generation-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--color-text);
  margin-bottom: 4px;
}

.generation-subtitle {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.generation-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.generation-step {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 10px;
  background: var(--color-bg);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  font-size: 12px;
}

.generation-step.active {
  color: var(--color-text);
  background: rgba(248, 198, 63, 0.14);
}

.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-border);
  flex-shrink: 0;
}

.generation-step.active .step-dot {
  background: var(--color-primary);
}

.generation-skeleton {
  padding: 16px;
  border-radius: var(--radius-sm);
  background: var(--color-bg);
}

.skeleton-line {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #edf1f5 0%, #f8fafc 50%, #edf1f5 100%);
  background-size: 200% 100%;
  animation: skeletonPulse 1.4s ease-in-out infinite;
  margin-bottom: 10px;
}

.skeleton-line.wide { width: 86%; }
.skeleton-line:not(.wide):not(.short) { width: 68%; }
.skeleton-line.short { width: 42%; margin-bottom: 0; }

@keyframes skeletonPulse {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.report-module-shell {
  margin: 0;
  position: relative;
  z-index: 2;
}

.module-switcher {
  display: flex;
  align-items: flex-end;
  gap: 34px;
  min-height: 58px;
  padding: 0 24px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-bottom: 0;
  border-top: 0;
  border-radius: 0;
  overflow-x: auto;
}

.module-tab {
  position: relative;
  appearance: none;
  border: 0;
  height: 58px;
  padding: 0;
  border-radius: 0;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
  font-size: 15px;
  font-weight: 800;
  text-align: left;
  white-space: nowrap;
}

.module-tab:hover {
  color: var(--color-text);
}

.module-tab.active {
  color: var(--color-text);
}

.module-tab.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--color-primary);
}

.module-label {
  flex: 1;
  min-width: 0;
}

.module-count {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
  color: var(--color-text-muted);
  font-size: 12px;
}

.module-tab.active .module-count {
  background: var(--color-primary);
  color: var(--color-text);
}

.report-item-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.report-item-card {
  display: flex;
  gap: 14px;
  padding: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.report-item-card:hover {
  border-color: rgba(248, 198, 63, 0.55);
  box-shadow: var(--shadow-sm);
}

.item-index {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(248, 198, 63, 0.16);
  color: var(--color-primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}

.item-body {
  min-width: 0;
  flex: 1;
}

.item-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--color-text);
  margin-bottom: 8px;
}

.item-content {
  font-size: 13px;
  line-height: 1.8;
  color: var(--color-text-secondary);
}

.item-action {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin-top: 10px;
  color: var(--color-text);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.item-action::after {
  content: " →";
  color: var(--color-primary-dark);
}

/* ===== Hero 综合信息卡 ===== */
.hero-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-bottom: 0;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 8px 22px rgba(36, 50, 68, 0.05);
  margin-bottom: 0;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--color-primary-gradient);
  color: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 700;
  flex-shrink: 0;
}

.hero-name {
  font-size: 22px;
  font-weight: 800;
  color: var(--color-text);
  margin: 0 0 4px 0;
}

.hero-meta {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 3px;
}

.report-authority {
  padding: 14px 24px;
  background: #fffdf6;
  border: 1px solid var(--color-border);
  border-top: 0;
  border-bottom: 0;
  color: var(--color-text-secondary);
  font-size: 13px;
  cursor: help;
}

.report-authority span {
  color: var(--color-text);
  font-weight: 800;
}

/* ===== 环形仪表盘 ===== */
.hero-gauge {
  display: flex;
  align-items: center;
  gap: 16px;
}

.gauge-ring {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.gauge-ring svg {
  transform: rotate(-90deg);
}

.gauge-bg {
  fill: none;
  stroke: var(--color-bg);
  stroke-width: 8;
}

.gauge-fill {
  fill: none;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 282.74;
  transition: stroke-dashoffset 0.8s ease;
}

.gauge-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.gauge-num {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  color: var(--color-text);
}

.gauge-label {
  font-size: 11px;
  color: var(--color-text-muted);
  margin-top: 2px;
}

.gauge-risk {
  text-align: center;
}

.risk-tag {
  display: inline-block;
  padding: 5px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
}

.risk-1 { background: var(--color-low-bg); color: var(--color-low-text); }
.risk-2 { background: var(--color-medium-bg); color: var(--color-medium-text); }
.risk-3 { background: var(--color-high-bg); color: var(--color-high-text); }

.risk-hint {
  font-size: 11px;
  color: var(--color-text-muted);
  margin-top: 4px;
}

.btn-action {
  padding: 7px 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  font-size: 13px;
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
}

.history-top-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid rgba(248, 198, 63, 0.7);
  border-radius: 8px;
  background: var(--color-primary);
  color: var(--color-text);
  font-size: 13px;
  font-weight: 800;
}

.btn-action:hover {
  border-color: var(--color-border);
  background: var(--color-bg);
}

.share-top-btn {
  flex-shrink: 0;
}

/* ===== 卡片通用 ===== */
.report-card {
  background: var(--color-surface);
  border-radius: 0 0 12px 12px;
  box-shadow: 0 8px 22px rgba(36, 50, 68, 0.05);
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-top: 0;
}

.report-module-shell + .report-card {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 10px;
}

.card-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
}

.gold-line {
  width: 48px;
  height: 3px;
  background: var(--color-primary);
  margin-left: 22px;
  border-radius: 2px;
}

.card-body {
  padding: 18px 22px 22px;
}

/* ===== 风险归因 ===== */
.risk-summary {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin-bottom: 18px;
  padding: 12px 14px;
  background: var(--color-high-bg);
  border-radius: var(--radius-md);
  border-left: 3px solid var(--color-high);
}

.risk-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.risk-bar-item {
  display: grid;
  grid-template-columns: minmax(230px, 280px) minmax(220px, 1fr) 42px;
  align-items: center;
  gap: 12px;
}

.bar-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  text-align: right;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
}

.bar-track {
  flex: 1;
  height: 10px;
  background: var(--color-bg);
  border-radius: 5px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.6s ease;
}

.bar-val {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  text-align: right;
}

.risk-reasons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.risk-reason {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: var(--color-bg);
  border-radius: 8px;
}

.reason-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--color-high-bg);
  color: var(--color-high-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.reason-text {
  flex: 1;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

/* ===== 管理建议卡片 ===== */
.suggestion-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.suggestion-card {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  transition: all 0.2s;
  cursor: pointer;
}

.suggestion-card:hover {
  border-color: var(--color-border);
  box-shadow: var(--shadow-sm);
  transform: translateY(-1px);
}

.suggestion-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 4px;
}

.suggestion-content {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.suggestion-action {
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 600;
  margin-top: 8px;
}

/* ===== 成功案例 ===== */
.case-block {
  padding: 14px 16px;
  background: var(--color-bg);
  border-radius: 10px;
  border-left: 3px solid var(--color-border);
}

.case-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 6px;
}

.case-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.7;
}

/* ===== 推荐课程 ===== */
.course-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.course-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: all 0.2s;
  cursor: pointer;
}

.course-row:hover {
  background: var(--color-bg);
}

.course-index {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-primary);
  width: 24px;
  flex-shrink: 0;
}

.course-info {
  flex: 1;
}

.course-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
}

.course-desc {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: 1px;
}

.course-arrow {
  font-size: 14px;
  color: var(--color-border);
  transition: color 0.2s;
}

.course-row:hover .course-arrow {
  color: var(--color-primary);
}

/* ===== FAQ ===== */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faq-item {
  padding: 12px 14px;
  background: var(--color-bg);
  border-radius: 8px;
  border-left: 3px solid var(--color-border);
}

.faq-question {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 6px;
}

.faq-answer {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.7;
}

/* ===== 行为回答详情 ===== */
.behavior-answers {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.behavior-answer-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
}

.behavior-answer-item.answer-1 {
  background: rgba(239, 68, 68, 0.04);
  border-color: rgba(239, 68, 68, 0.15);
}

.behavior-answer-item.answer-2 {
  background: rgba(16, 185, 129, 0.04);
  border-color: rgba(16, 185, 129, 0.15);
}

.behavior-answer-item.answer-3 {
  background: var(--color-bg);
}

.answer-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--color-bg);
  color: var(--color-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.answer-desc {
  flex: 1;
  font-size: 13px;
  color: var(--color-text);
  line-height: 1.5;
}

.section-count {
  font-size: 12px;
  color: var(--color-text-muted);
  font-weight: 500;
  background: var(--color-bg);
  padding: 2px 10px;
  border-radius: 10px;
}

/* ===== 命中行为标签 ===== */
.behavior-tags-section {
  margin-top: 18px;
  padding: 12px 14px;
  background: var(--color-surface);
  border-radius: 8px;
  border-left: 3px solid var(--color-primary);
}

.behavior-tags-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-medium-text);
  margin-bottom: 8px;
}

.behavior-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.behavior-tag {
  font-size: 12px;
}

/* ===== 打印样式 ===== */
@media print {
  .page-back-header,
  .report-top-actions {
    display: none !important;
  }

  .report-page {
    padding: 0;
    max-width: 100%;
  }

  .report-card {
    box-shadow: none;
    border: 1px solid var(--color-border);
    break-inside: avoid;
  }
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .report-page {
    padding: 16px 12px 32px;
  }

  .report-headline {
    align-items: flex-start;
  }

  .report-top-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .share-top-btn {
    padding-left: 12px;
    padding-right: 12px;
  }

  .hero-card {
    padding: 18px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .hero-gauge {
    align-self: flex-end;
  }

  .generation-panel {
    padding: 18px 16px;
  }

  .generation-steps {
    grid-template-columns: 1fr;
  }

  .module-switcher {
    gap: 22px;
    padding: 0 16px;
  }

  .risk-bar-item {
    grid-template-columns: minmax(150px, 1.1fr) minmax(120px, 0.9fr) 36px;
  }

  .report-item-card {
    padding: 14px;
  }

  .gauge-ring {
    width: 80px;
    height: 80px;
  }

  .gauge-num {
    font-size: 22px;
  }

  .card-header,
  .card-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .gold-line {
    margin-left: 16px;
  }
}
</style>
